import { useRsrc } from '@/common/rsrc/RsrcContext';
import { DateUtils } from '@/common/utils/DateUtils';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

const ENTITY = 'PYMT';

// Current OOO window for a username, if any; mirrors ApprRouteUserSvc.outOfOfficeUsernames
const activeOoo = (usernameToOoos, username, today) =>
  (usernameToOoos[username] ?? []).find((ooo) => ooo.startDate <= today && today <= ooo.endDate);

// Mirrors ApprRouteUserSvc.assignApprovers: slots in route order; own group by priority, else cascade to more senior groups.
// Returns routed: username -> group whose slot they fill (union across routes; a given payment only follows one),
// and unfilled: routes with a slot nobody can take today.
const simulateRouting = (routes, groupToRows, seniority, isOoo) => {
  const routed = {};
  const unfilled = [];
  routes.forEach((route) => {
    const taken = new Set();
    route.steps.forEach((step) =>
      Object.entries(step.apprGroupToJobIds).forEach(([group, jobIds]) => {
        const rank = seniority.indexOf(group);
        const tryGroups = [group, ...(rank >= 0 ? seniority.slice(rank + 1) : [])];
        jobIds.forEach(() => {
          for (const g of tryGroups) {
            const pick = (groupToRows[g] ?? []).find((row) => !isOoo(row) && !taken.has(row.username));
            if (pick) {
              taken.add(pick.username);
              routed[pick.username] = group;
              return;
            }
          }
          unfilled.push({ route: route.name, group });
        });
      }),
    );
  });
  return { routed, unfilled };
};

export const PymtApprovalDialog = ({ closeDialog }) => {
  const rsrc = useRsrc();
  const theme = useTheme();

  // apprGroup -> number of approvers the PYMT routes require from that group (max across routes/steps)
  const [groupToRequired, setGroupToRequired] = useState();
  // apprGroup -> candidate users from the UAA group of the same code
  const [groupToUsers, setGroupToUsers] = useState();
  // Persisted APPR_ROUTE_USER rows as loaded, used to diff on submit
  const [origRows, setOrigRows] = useState();
  // apprGroup -> ordered list of rows; index is the approval priority
  const [groupToRows, setGroupToRows] = useState();
  // username -> OOO windows
  const [usernameToOoos, setUsernameToOoos] = useState({});
  const [routes, setRoutes] = useState([]);
  const [seniority, setSeniority] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const routesPromise = rsrc.data('skdr', 'APPR_ROUTE_DATA_PLUGIN', { entity: ENTITY, entityObjs: [] }, ENTITY);
    const usersPromise = rsrc.data('admin', 'GROUP_TO_USERS_DATA_PLUGIN', {});
    const rowsPromise = rsrc.rows('skdr', 'APPR_ROUTE_USER', { expr: ExprUtils.whereParams('entity', ENTITY) });
    const ooosPromise = rsrc.rows('skdr', 'APPR_ROUTE_USER_OOO', {});
    const seniorityPromise = rsrc.data('skdr', 'APPR_GROUP_SENIORITY_DATA_PLUGIN', {});

    Promise.all([routesPromise, usersPromise, rowsPromise, ooosPromise, seniorityPromise]).then(
      ([routes, users, rows, ooos, seniority]) => {
        setRoutes(routes);
        setSeniority(seniority);
        const byUsername = {};
        ooos.forEach((ooo) => (byUsername[ooo.username] = [...(byUsername[ooo.username] ?? []), ooo]));
        setUsernameToOoos(byUsername);

        // A route's demand on a group is the total across its steps; the dialog must satisfy the largest route
        const required = {};
        routes.forEach((route) => {
          const routeRequired = {};
          route.steps.forEach((step) =>
            Object.entries(step.apprGroupToJobIds).forEach(([group, jobIds]) => {
              routeRequired[group] = (routeRequired[group] ?? 0) + jobIds.length;
            }),
          );
          Object.entries(routeRequired).forEach(([group, count]) => {
            required[group] = Math.max(required[group] ?? 0, count);
          });
        });
        setGroupToRequired(required);
        setGroupToUsers(users);
        setOrigRows(rows);

        const byGroup = {};
        Object.keys(required).forEach((group) => (byGroup[group] = []));
        rows.forEach((row) => {
          // Rows for a group no longer referenced by any route are still shown so they can be removed
          if (!byGroup[row.apprGroup]) byGroup[row.apprGroup] = [];
          byGroup[row.apprGroup].push(row);
        });
        Object.values(byGroup).forEach((list) =>
          list.sort((a, b) => (a.approvalPriority ?? 0) - (b.approvalPriority ?? 0)),
        );
        setGroupToRows(byGroup);
      },
    );
  }, []);

  const usernameToUser = useMemo(() => {
    const map = {};
    Object.values(groupToUsers ?? {}).forEach((users) => users.forEach((user) => (map[user.username] = user)));
    return map;
  }, [groupToUsers]);

  const handleDragEnd = ({ source, destination }) => {
    if (!destination || source.droppableId !== destination.droppableId) return;
    setGroupToRows((current) => {
      const list = Array.from(current[source.droppableId]);
      const [moved] = list.splice(source.index, 1);
      list.splice(destination.index, 0, moved);
      return { ...current, [source.droppableId]: list };
    });
  };

  const addUser = (group, username) => {
    setGroupToRows((current) => ({
      ...current,
      [group]: [...current[group], { username, apprGroup: group, entity: ENTITY }],
    }));
  };

  const removeUser = (group, index) => {
    setGroupToRows((current) => {
      const list = Array.from(current[group]);
      list.splice(index, 1);
      return { ...current, [group]: list };
    });
  };

  const submit = () => {
    const toSave = [];
    const keptIds = new Set();

    Object.values(groupToRows).forEach((list) =>
      list.forEach((row, index) => {
        const approvalPriority = index + 1;
        if (row.id) keptIds.add(row.id);
        if (!row.id || row.approvalPriority !== approvalPriority) {
          toSave.push({ ...row, approvalPriority });
        }
      }),
    );

    const toDelete = origRows.filter((row) => !keptIds.has(row.id));

    setSaving(true);
    const savePromise = toSave.length > 0 ? rsrc.save('skdr', 'APPR_ROUTE_USER', toSave) : Promise.resolve();
    Promise.all([savePromise, ...toDelete.map((row) => rsrc.delete('skdr', 'APPR_ROUTE_USER', row.id))])
      .then(() => closeDialog({ submitted: true }))
      .finally(() => setSaving(false));
  };

  // Junior to senior so the cascade reads left to right; groups outside the seniority list go last
  const groups =
    groupToRows ?
      Object.keys(groupToRows).sort((a, b) => {
        const rankA = seniority.indexOf(a);
        const rankB = seniority.indexOf(b);
        if (rankA >= 0 && rankB >= 0) return rankA - rankB;
        if (rankA >= 0) return -1;
        if (rankB >= 0) return 1;
        return a.localeCompare(b);
      })
    : [];

  // Every route must be satisfiable, so each group must carry at least the largest count any route asks of it
  const understaffedGroups = groups.filter(
    (group) => groupToRequired[group] != null && groupToRows[group].length < groupToRequired[group],
  );

  const today = Number(DateUtils.today());
  const isOoo = (row) => Boolean(activeOoo(usernameToOoos, row.username, today));
  const { routed: routedToday, unfilled } =
    groupToRows ? simulateRouting(routes, groupToRows, seniority, isOoo) : { routed: {}, unfilled: [] };
  const invalid = understaffedGroups.length > 0 || unfilled.length > 0;
  // Cascade is the only path to a second group, so a user may sit in one column only
  const assignedUsernames = new Set(
    Object.values(groupToRows ?? {}).flatMap((rows) => rows.map((row) => row.username)),
  );

  return (
    groupToRows && (
      <Dialog open fullWidth maxWidth="lg" onClose={() => closeDialog()} data-test-id="pymt-approval-dialog">
        <DialogTitle>Payment Approval Structure</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            Add Approvers to Rank by priority. The highlighted names show who would receive a payment sent for approval
            today if it needed every group. Note that smaller payments require less approvers.
          </Typography>

          {groups.length === 0 && (
            <Typography>No approval routes are configured for payments, so there are no groups to staff.</Typography>
          )}

          {understaffedGroups.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {understaffedGroups.map((group) => `${group} needs at least ${groupToRequired[group]}`).join('; ')}{' '}
              approver{understaffedGroups.some((group) => groupToRequired[group] > 1) ? 's' : ''} to satisfy every
              payment approval route.
            </Alert>
          )}

          {unfilled.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }} data-test-id="pymt-approval-unfilled">
              With today's out-of-office approvers, nobody is left to fill{' '}
              {unfilled.map(({ route, group }) => ` "${route}"`).join('; ')}.
            </Alert>
          )}

          <DragDropContext onDragEnd={handleDragEnd}>
            <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 1 }}>
              {groups.map((group) => (
                <GroupColumn
                  key={group}
                  group={group}
                  required={groupToRequired[group]}
                  rows={groupToRows[group]}
                  candidates={(groupToUsers[group] ?? []).filter((user) => !assignedUsernames.has(user.username))}
                  usernameToUser={usernameToUser}
                  usernameToOoos={usernameToOoos}
                  routedToday={routedToday}
                  onAdd={(username) => addUser(group, username)}
                  onRemove={(index) => removeUser(group, index)}
                  theme={theme}
                />
              ))}
            </Box>
          </DragDropContext>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="secondary" onClick={() => closeDialog()}>
            Cancel
          </Button>
          <Button variant="contained" onClick={submit} disabled={saving || invalid} data-test-id="pymt-approval-submit">
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    )
  );
};

const GroupColumn = ({
  group,
  required,
  rows,
  candidates,
  usernameToUser,
  usernameToOoos,
  routedToday,
  onAdd,
  onRemove,
  theme,
}) => {
  const understaffed = required != null && rows.length < required;
  const orphaned = required == null;
  const today = Number(DateUtils.today());
  const isOutOfOffice = (row) => Boolean(activeOoo(usernameToOoos, row.username, today));
  const availableToday = rows.filter((row) => !isOutOfOffice(row)).length;
  const shortToday = !understaffed && required != null && availableToday < required;
  const inColumn = new Set(rows.map((row) => row.username));
  // Users from a more senior column routed into this group's slots today
  const coveredBy = Object.entries(routedToday)
    .filter(([username, target]) => target === group && !inColumn.has(username))
    .map(([username]) => username);
  const covered = shortToday && coveredBy.length > 0;

  return (
    <Box
      sx={{
        flex: '1 1 0',
        minWidth: 340,
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid',
        borderColor:
          understaffed ? 'error.main'
          : shortToday && !covered ? 'warning.main'
          : 'divider',
        borderRadius: '16px',
        bgcolor: theme.palette.surface.main,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ px: 2, pt: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Typography variant="subtitle1">{group}</Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {orphaned ?
            <Tooltip title="No payment approval route references this group">
              <Chip size="small" label="Unused" />
            </Tooltip>
          : <Tooltip
              title={`The largest payment route requires ${required} approver${required > 1 ? 's' : ''} from this group`}
            >
              <Chip
                size="small"
                color={understaffed ? 'error' : 'default'}
                label={`${rows.length} / ${required} required`}
              />
            </Tooltip>
          }
          {shortToday && !covered && (
            <Tooltip title="Out-of-office approvers leave too few available to route the largest payments today">
              <Chip size="small" color="warning" label={`${availableToday} available`} />
            </Tooltip>
          )}
          {covered && (
            <Tooltip title={`Out-of-office approvers are covered today by: ${coveredBy.join(', ')}`}>
              <Chip size="small" color="primary" variant="outlined" label="Covered" />
            </Tooltip>
          )}
        </Box>
      </Box>

      <Droppable droppableId={group}>
        {(provided, snapshot) => (
          <List
            {...provided.droppableProps}
            ref={provided.innerRef}
            sx={{
              flex: 1,
              m: 2,
              mb: 1,
              p: 1,
              minHeight: 120,
              bgcolor: theme.palette.surface.low,
              borderRadius: '4px',
              border: 1,
              borderColor: snapshot.isDraggingOver ? alpha(theme.palette.surface.tint, 0.16) : 'transparent',
              transition: 'border-color 0.2s ease',
            }}
          >
            {rows.length === 0 && (
              <Typography variant="body2" sx={{ color: 'text.disabled', textAlign: 'center', py: 2 }}>
                No approvers configured
              </Typography>
            )}
            {rows.map((row, index) => (
              <ApproverItem
                key={row.id ?? row.username}
                row={row}
                index={index}
                isPrimary={row.username in routedToday}
                coveringGroup={routedToday[row.username] !== group ? routedToday[row.username] : null}
                user={usernameToUser[row.username]}
                ooo={activeOoo(usernameToOoos, row.username, today)}
                onRemove={() => onRemove(index)}
                theme={theme}
              />
            ))}
            {provided.placeholder}
          </List>
        )}
      </Droppable>

      <Box sx={{ px: 2, pb: 2 }}>
        <FormControl variant="filled" size="small" fullWidth disabled={candidates.length === 0}>
          <InputLabel>Add approver</InputLabel>
          <Select value="" onChange={(event) => onAdd(event.target.value)} data-test-id={`add-approver-${group}`}>
            {candidates.map((user) => (
              <MenuItem key={user.username} value={user.username}>
                {user.firstName} {user.lastName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
};

const ApproverItem = ({ row, index, isPrimary, coveringGroup, user, ooo, onRemove, theme }) => {
  const isOoo = Boolean(ooo);
  const displayName = user ? `${user.firstName} ${user.lastName}` : row.username;

  return (
    <Draggable draggableId={`${row.apprGroup}-${row.username}`} index={index}>
      {(prov, snapshot) => (
        <ListItem
          ref={prov.innerRef}
          {...prov.draggableProps}
          {...prov.dragHandleProps}
          secondaryAction={
            <IconButton edge="end" size="small" onClick={onRemove} data-test-id={`remove-approver-${row.username}`}>
              <FontAwesomeIcon icon={fas.faXmark} />
            </IconButton>
          }
          sx={{
            mb: 1,
            borderRadius: '4px',
            bgcolor: snapshot.isDragging ? alpha(theme.palette.surface.tint, 0.16) : theme.palette.surface.container,
            borderLeft: '4px solid',
            borderLeftColor: isPrimary ? 'primary.main' : 'transparent',
            opacity: isOoo ? 0.6 : 1,
            boxShadow: snapshot.isDragging ? 3 : 0,
            transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
            '&:hover': { cursor: 'grab', bgcolor: alpha(theme.palette.surface.tint, 0.08) },
          }}
        >
          <ListItemIcon sx={{ minWidth: 36, color: 'text.secondary' }}>
            <FontAwesomeIcon icon={fas.faGripVertical} />
          </ListItemIcon>
          <Chip
            size="small"
            label={index + 1}
            sx={{ mr: 1.5, minWidth: 32 }}
            color={isPrimary ? 'primary' : 'default'}
          />
          <ListItemText
            primary={displayName}
            sx={{ pr: 4 }}
            secondary={
              <Box
                component="span"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 0.5,
                  '& .MuiChip-root': { flexShrink: 0, maxWidth: 'none' },
                  '& .MuiChip-label': { overflow: 'visible' },
                }}
              >
                {row.username}
                {!user && <Chip size="small" color="error" label="Not in group" />}
                {coveringGroup && <Chip size="small" color="primary" label={`Covering ${coveringGroup}`} />}
                {isOoo && <Chip size="small" color="warning" label={`OOO until ${DateUtils.format(ooo.endDate)}`} />}
              </Box>
            }
            secondaryTypographyProps={{ component: 'div' }}
          />
        </ListItem>
      )}
    </Draggable>
  );
};
